import * as React from "react";
import { CalendarIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { formatDate } from "@/lib/datetime";
import { cn } from "@/lib/utils";

// Same "YYYY-MM-DD" value shape every call site already used for a plain
// <input type="date">: no time-of-day component, since a date like a
// bill's own transaction date has none - only the shadcn Calendar UI
// changes here, not the data flow, so this drops in without touching
// submit logic (see DateTimePicker.tsx for the equivalent datetime-local
// version of this same swap-in).
function parseLocal(dateOnly: string): Date | undefined {
  if (!dateOnly) return undefined;
  const d = new Date(`${dateOnly}T00:00:00`);
  return Number.isNaN(d.getTime()) ? undefined : d;
}

function toDateOnlyString(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function DatePicker({
  value,
  onChange,
  placeholder = "Pick a date",
  disabled,
  maxDate,
  minDate
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Days after this date are unselectable. */
  maxDate?: Date;
  /** Days before this date are unselectable. */
  minDate?: Date;
}) {
  const [open, setOpen] = React.useState(false);
  const selected = parseLocal(value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={cn(
            "w-full justify-start font-normal",
            !selected && "text-muted-foreground"
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
          {selected ? formatDate(selected) : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selected}
          onSelect={(date) => {
            if (date) {
              onChange(toDateOnlyString(date));
              setOpen(false);
            }
          }}
          disabled={[
            ...(maxDate ? [{ after: maxDate }] : []),
            ...(minDate ? [{ before: minDate }] : [])
          ]}
          autoFocus
        />
      </PopoverContent>
    </Popover>
  );
}
